import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { SETTINGS_SELECT_CONTENT_CLASS, SETTINGS_SELECT_ITEM_CLASS, SETTINGS_SELECT_TRIGGER_CLASS } from './settings-ui';

export interface SettingsMultiSelectOption {
	value: string;
	label: ReactNode;
	/** Plain text for the closed trigger's summary. */
	text: string;
}

interface SettingsMultiSelectProps {
	values: string[];
	onValuesChange: (values: string[]) => void;
	options: SettingsMultiSelectOption[];
	/** Shown when nothing is picked, which means every option. */
	allLabel: string;
	/**
	 * Off where nothing picked means nothing rather than every option (a playbook's "is any of"):
	 * the "All" row is left out and `allLabel` reads as the placeholder.
	 */
	showAllOption?: boolean;
	disabled?: boolean;
	invalid?: boolean;
	'aria-label': string;
	className?: string;
}

/** The closed trigger's text: "All …", the one pick, or the first pick and how many more. */
function multiSelectSummary(values: string[], options: SettingsMultiSelectOption[], allLabel: string): string {
	const [first, ...rest] = values;
	if (first === undefined) return allLabel;
	const firstText = options.find((option) => option.value === first)?.text ?? first;
	return rest.length ? `${firstText} +${rest.length}` : firstText;
}

/**
 * A settings multi-select: the settings trigger with a checkable list. Nothing picked means every
 * option, and the "All" row clears the picks.
 */
export function SettingsMultiSelect({
	values,
	onValuesChange,
	options,
	allLabel,
	showAllOption = true,
	disabled,
	invalid,
	className,
	...rest
}: SettingsMultiSelectProps) {
	const [open, setOpen] = useState(false);
	const toggle = (value: string) => onValuesChange(values.includes(value) ? values.filter((picked) => picked !== value) : [...values, value]);
	const row = (key: string, label: ReactNode, selected: boolean, onSelect: () => void) => (
		<button
			key={key}
			type='button'
			role='option'
			aria-selected={selected}
			onClick={onSelect}
			className={cn(SETTINGS_SELECT_ITEM_CLASS, 'flex w-full items-center gap-2 pr-2.5 text-left', selected && 'bg-ink/8')}
		>
			<span className='min-w-0 flex-1'>{label}</span>
			<Check aria-hidden='true' className={cn('size-4 shrink-0', !selected && 'invisible')} />
		</button>
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type='button'
					role='combobox'
					aria-label={rest['aria-label']}
					aria-expanded={open}
					aria-haspopup='listbox'
					aria-invalid={invalid || undefined}
					disabled={disabled}
					className={cn(
						SETTINGS_SELECT_TRIGGER_CLASS,
						'flex cursor-pointer items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60',
						!values.length && !showAllOption && 'text-gray-800',
						className
					)}
				>
					<span className='min-w-0 flex-1 truncate'>{multiSelectSummary(values, options, allLabel)}</span>
					<ChevronDown aria-hidden='true' className='size-4 shrink-0 text-gray-800' />
				</button>
			</PopoverTrigger>
			<PopoverContent align='start' className={cn(SETTINGS_SELECT_CONTENT_CLASS, 'w-[var(--radix-popover-trigger-width)] p-1')}>
				<div role='listbox' aria-label={`${rest['aria-label']} options`} aria-multiselectable='true' className='flex max-h-72 flex-col gap-0.5 overflow-y-auto'>
					{showAllOption && row('all', allLabel, values.length === 0, () => onValuesChange([]))}
					{options.map((option) => row(option.value, option.label, values.includes(option.value), () => toggle(option.value)))}
				</div>
			</PopoverContent>
		</Popover>
	);
}
